import React, { useState } from 'react';
import SplashScreen from './SplashScreen';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast
} from '@chakra-ui/react';
import { FaKey, FaLock, FaUser } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';

const Login = ({ setUser }) => {
  const sharedInputStyles = {
    bg: 'white',
    border: '1px solid rgba(255, 255, 255, 0.6)',
    color: '#0b1725',
    fontSize: { base: '16px', md: '18px' },
    letterSpacing: '0.01em',
    fontWeight: '600',
    borderRadius: '16px',
    boxShadow: '0 16px 35px rgba(10, 31, 75, 0.4)',
    px: 4,
    py: 4,
    transition: 'all 0.2s ease',
    _hover: { borderColor: '#ffffff' },
    _placeholder: {
      color: 'rgba(11, 23, 37, 0.65)',
      fontWeight: '500',
      fontSize: { base: '14px', md: '15px' },
      letterSpacing: '0.005em'
    },
    _focusVisible: {
      borderColor: '#ffffff',
      boxShadow: '0 0 0 3px rgba(255,255,255,0.35)'
    },
    sx: {
      '@keyframes pulsePlaceholder': {
        '0%': { opacity: 0.45, transform: 'translateY(0)' },
        '50%': { opacity: 0.85, transform: 'translateY(-1px)' },
        '100%': { opacity: 0.45, transform: 'translateY(0)' }
      },
      '&::placeholder': {
        animation: 'pulsePlaceholder 2.2s ease-in-out infinite'
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [showSplash, setShowSplash] = useState(false);
  const [cim, setCim] = useState('');
  const [cpf, setCpf] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [pendingUser, setPendingUser] = useState(null);
  const toast = useToast();
  const navigate = useNavigate();
  const firstAccessModal = useDisclosure();
  const passwordModal = useDisclosure();

  const handleCimChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setCim(value);
  };

  const handleCpfChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 11) {
      const formattedCpf = value
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
      setCpf(formattedCpf);
    }
  };

  const resetPasswordState = () => {
    setAttempts(0);
    setPassword('');
    setPasswordError('');
  };

  const handleTooManyAttempts = (message) => {
    toast({
      title: 'Senha incorreta',
      description: message,
      status: 'error',
      duration: 5000,
      isClosable: true
    });
    resetPasswordState();
    setCim('');
    setCpf('');
    setError('');
    passwordModal.onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API_BASE}/auth/check-first-access`, { cim, cpf });
      const data = response.data || {};

      if (!data.userExists) {
        setError('CIM ou CPF incorreto');
        toast({
          title: 'Erro no login',
          description: data.message || 'CIM ou CPF incorreto. Tente novamente.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setIsLoading(false);
        return;
      }

      const accessData = {
        userId: data.userId,
        cim,
        cpf,
        nome: data.user?.nome
      };
      setPendingUser(accessData);

      if (data.firstAccess) {
        sessionStorage.setItem('pending_access', JSON.stringify(accessData));
        firstAccessModal.onOpen();
      } else {
        resetPasswordState();
        passwordModal.onOpen();
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor. Tente novamente mais tarde.');
      toast({
        title: 'Erro no servidor',
        description: 'Não foi possível conectar ao servidor.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError('Informe a senha.');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    try {
      const loginResp = await axios.post(`${API_BASE}/auth/login`, {
        cim: pendingUser?.cim || cim,
        cpf: pendingUser?.cpf || cpf,
        password
      });

      const userData = loginResp.data?.user || loginResp.data?.userData || loginResp.data?.result?.[0]?.[0];
      if (!userData) {
        throw new Error('Usuario nao retornado.');
      }

      sessionStorage.setItem('user', JSON.stringify(userData));
      sessionStorage.removeItem('pending_access');
      setUser(userData);
      setShowSplash(true);
      passwordModal.onClose();
      setTimeout(() => {
        navigate('/');
      }, 4000);
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error || 'Senha incorreta. Tente novamente.';

      if (status === 429) {
        handleTooManyAttempts(message);
        setPasswordLoading(false);
        return;
      }

      if (status && status >= 500) {
        toast({
          title: 'Erro no servidor',
          description: message,
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setPasswordLoading(false);
        return;
      }

      const nextAttempts = attempts + 1;
      setAttempts(nextAttempts);
      if (nextAttempts >= 3) {
        handleTooManyAttempts('Você errou a senha 3 vezes. Tente novamente.');
      } else {
        setPasswordError(message);
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (showSplash) {
    return <SplashScreen />;
  }

  return (
    <Box minH="100vh" position="relative" overflow="hidden">
      <Box
        position="absolute"
        inset="0"
        backgroundImage="url('/fundo.jpeg')"
        backgroundSize="cover"
        backgroundPosition="52% 18%"
      />
      <Box
        position="absolute"
        inset="0"
        bg="linear-gradient(180deg, rgba(20,50,100,0.38), rgba(10,26,36,0.62))"
      />

      <Flex
        position="relative"
        zIndex={1}
        minH="100vh"
        direction="column"
        align={{ base: 'stretch', md: 'center' }}
        justify={{ base: 'space-between', md: 'space-between' }}
        px={{ base: 4, sm: 6, md: 10 }}
        py={{ base: 8, md: 10 }}
      >
        <VStack spacing={1} align="center" mt={{ base: 4, md: 8 }}>
          {['LOJA MAÇONICA', 'ESTRELA CALDENSE'].map((line) => (
            <Text
              key={line}
              fontSize={{ base: '24px', md: '28px' }}
              fontFamily="Marcellus"
              textTransform="uppercase"
              letterSpacing="0.18em"
              color="white"
              textShadow="0 3px 14px rgba(0,0,0,0.65)"
            >
              {line}
            </Text>
          ))}
        </VStack>

        <Box flex="1" />

        <Box width="100%" maxW={{ base: '100%', sm: '460px', md: '480px' }} mb={{ base: 6, md: 0 }}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={4} align="stretch">
              <FormControl id="cim" isRequired>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaUser color="#1F4B99" size="18" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    value={cim}
                    onChange={handleCimChange}
                    placeholder="Digite seu CIM"
                    maxLength="6"
                    {...sharedInputStyles}
                  />
                </InputGroup>
              </FormControl>

              <FormControl id="cpf" isRequired>
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <FaKey color="#1F4B99" size="18" />
                  </InputLeftElement>
                  <Input
                    type="text"
                    value={cpf}
                    onChange={handleCpfChange}
                    placeholder="Digite seu CPF"
                    {...sharedInputStyles}
                  />
                </InputGroup>
              </FormControl>

              {error && (
                <Text color="red.500" fontSize="sm" textAlign="center">
                  {error}
                </Text>
              )}

              <Button
                type="submit"
                bg="#1F4B99"
                color="white"
                size="lg"
                width="full"
                borderRadius="18px"
                _hover={{ bg: '#163c7a' }}
                isLoading={isLoading}
                loadingText="Validando..."
                leftIcon={!isLoading ? <FaKey /> : undefined}
              >
                {isLoading ? 'Validando...' : 'ENTRAR'}
              </Button>
            </VStack>
          </form>
        </Box>
      </Flex>

      <Modal isOpen={firstAccessModal.isOpen} onClose={firstAccessModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="24px" p={2}>
          <ModalHeader>Primeiro acesso</ModalHeader>
          <ModalBody>
            <Text color="gray.600">
              Ir∴ {pendingUser?.nome ? `${pendingUser.nome}, ` : ''}
              identificamos que este é seu primeiro acesso. Crie sua senha para continuar.
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={firstAccessModal.onClose}>
              Voltar
            </Button>
            <Button
              bg="#1F4B99"
              color="white"
              _hover={{ bg: '#163c7a' }}
              onClick={() => {
                firstAccessModal.onClose();
                navigate('/criar-senha');
              }}
            >
              Criar senha
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={passwordModal.isOpen} onClose={passwordModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="24px" p={2}>
          <ModalHeader color="gray.600">Digite sua senha</ModalHeader>
          <ModalBody>
            <FormControl isInvalid={Boolean(passwordError)}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FaLock color="#1F4B99" size="18" />
                </InputLeftElement>
                

                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  {...sharedInputStyles}
                />
              </InputGroup>
              {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
            </FormControl>
            <Text color="gray.500" fontSize="xs" mt={3}>
              Tentativas: {attempts} / 3
            </Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={passwordModal.onClose}>
              Cancelar
            </Button>
            <Button
              bg="#1F4B99"
              color="white"
              _hover={{ bg: '#163c7a' }}
              onClick={handlePasswordSubmit}
              isLoading={passwordLoading}
              loadingText="Entrando..."
            >
              Entrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Login;
